import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  HostListener,
  Renderer2,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Subject, forkJoin, of } from "rxjs";
import { debounceTime, distinctUntilChanged, catchError, map } from "rxjs/operators";
import {
  ProjectIssuesService,
  IssueItem,
  IssueStatus,
  IssueTag,
  CreateIssuePayload,
  AgencyItem,
  IssueDetail,
  IssueCommentItem,
  IssueActivityType,
  CreateActivityPayload,
} from "../../../services/project-issues.service";

@Component({
  selector: "app-project-issues-drawer",
  templateUrl: "./project-issues-drawer.component.html",
  styleUrls: ["./project-issues-drawer.component.scss"],
})
export class ProjectIssuesDrawerComponent implements OnChanges {
  @Input() open: boolean = false;
  @Input() projectId: string = "";
  @Input() agencyId: string = "";
  @Input() projectName: string = "";

  @Output() closed = new EventEmitter<void>();

  issues: IssueItem[] = [];
  loading: boolean = false;
  errorMsg: string = "";

  viewMode: "list" | "create" | "detail" = "list";

  filterForm: FormGroup;
  createForm: FormGroup;
  commentForm: FormGroup;

  creating: boolean = false;
  createError: string = "";

  agencies: AgencyItem[] = [];
  agenciesLoading: boolean = false;
  agenciesError: string = "";

  selectedIssueId: number | null = null;
  issueDetail: IssueDetail | null = null;
  detailLoading: boolean = false;
  detailError: string = "";

  comments: IssueCommentItem[] = [];
  commentsLoading: boolean = false;
  commentsError: string = "";
  postingComment: boolean = false;
  deletingCommentId: number | null = null;

  commentsSummary: {
    [issueId: string]: { count: number; latest?: IssueCommentItem | null; loading?: boolean };
  } = {};

  private search$ = new Subject<string>();

  constructor(
    private fb: FormBuilder,
    private issuesService: ProjectIssuesService,
    private renderer: Renderer2
  ) {
    this.filterForm = this.fb.group({
      search: [""],
      status: [""],
    });

    this.createForm = this.fb.group({
      project: [{ value: "", disabled: true }, [Validators.required]],
      agency: ["", [Validators.required]],
      tag: ["PMG", [Validators.required]],
      title: [
        "",
        [Validators.required, Validators.pattern(/\S/), Validators.maxLength(200)],
      ],
      description: [
        "",
        [Validators.required, Validators.pattern(/\S/), Validators.maxLength(2000)],
      ],
      recipient_state: [
        "",
        [Validators.required, Validators.pattern(/\S/), Validators.maxLength(150)],
      ],
      responsible_authority: [
        "",
        [Validators.required, Validators.pattern(/\S/), Validators.maxLength(200)],
      ],
      issue_category: [
        "",
        [Validators.required, Validators.pattern(/\S/), Validators.maxLength(200)],
      ],
      status: ["Pending", [Validators.required]],
    });

    this.commentForm = this.fb.group({
      activity_type: ["comment", [Validators.required]],
      message: ["", [Validators.required, Validators.maxLength(2000)]],
    });

    this.search$
      .pipe(debounceTime(350), distinctUntilChanged())
      .subscribe(() => this.loadIssues());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["open"]) {
      this.toggleBodyScrollLock(this.open);
      if (this.open) this.loadIssues();
      if (!this.open) {
        this.viewMode = "list";
        this.resetAllStates();
      }
    }

    if (changes["projectId"] && this.open) {
      this.loadIssues();
      if (this.viewMode === "create") this.resetCreateForm();
    }

    if (changes["agencyId"] && this.open) {
      if (this.viewMode === "create") this.resetCreateForm();
    }
  }

  @HostListener("document:keydown.escape")
  onEsc(): void {
    if (!this.open) return;
    this.close();
  }

  close(): void {
    this.viewMode = "list";
    this.resetAllStates();
    this.closed.emit();
  }

  onBackdropClick(): void {
    this.close();
  }

  onSearchInput(value: string): void {
    this.search$.next((value || "").trim());
  }

  onStatusChange(): void {
    this.loadIssues();
  }

  refresh(): void {
    if (this.viewMode === "detail" && this.selectedIssueId) {
      this.loadIssueDetail(this.selectedIssueId);
      this.loadIssueComments(this.selectedIssueId);
      return;
    }
    this.loadIssues();
  }

  openCreate(): void {
    this.viewMode = "create";
    this.resetDetailState();
    this.resetCreateState();
    this.resetCreateForm();
    this.loadAgencies();
  }

  backToList(): void {
    this.viewMode = "list";
    this.resetCreateState();
    this.resetCreateForm();
    this.resetDetailState();
  }

  openIssueDetail(item: IssueItem): void {
    const id = Number(item?.id);
    if (!id) return;
    this.viewMode = "detail";
    this.resetCreateState();
    this.resetCreateForm();
    this.loadIssueDetail(id);
    this.loadIssueComments(id);
  }

  loadIssueDetail(issueId: number): void {
    this.selectedIssueId = issueId;
    this.issueDetail = null;
    this.detailLoading = true;
    this.detailError = "";

    this.issuesService.getIssueDetail(issueId).subscribe({
      next: (res) => {
        this.issueDetail = res || null;
        this.detailLoading = false;
      },
      error: (err) => {
        this.detailLoading = false;
        this.detailError =
          err?.error?.message ||
          err?.error?.detail ||
          "Failed to load issue details. Please try again.";
      },
    });
  }

  loadIssueComments(issueId: number): void {
    this.commentsLoading = true;
    this.commentsError = "";
    this.comments = [];

    this.issuesService.getIssueComments(issueId).subscribe({
      next: (items) => {
        const sorted = (items || []).slice().sort((a: any, b: any) => {
          const at = a?.created_at ? new Date(a.created_at).getTime() : 0;
          const bt = b?.created_at ? new Date(b.created_at).getTime() : 0;
          return bt - at;
        });

        this.comments = sorted;
        this.commentsLoading = false;

        this.updateCommentsSummary(issueId, sorted);
      },
      error: () => {
        this.commentsLoading = false;
        this.commentsError = "Failed to load comments.";
      },
    });
  }

  submitComment(): void {
    if (this.postingComment) return;
    if (!this.selectedIssueId) return;

    this.commentsError = "";

    if (this.commentForm.invalid) {
      this.commentForm.markAllAsTouched();
      return;
    }

    const raw = this.commentForm.getRawValue();
    const payload: CreateActivityPayload = {
      issue: Number(this.selectedIssueId),
      activity_type: raw.activity_type as IssueActivityType,
      message: (raw.message || "").trim(),
    };

    if (!payload.message) return;

    this.postingComment = true;

    this.issuesService.createIssueActivity(payload).subscribe({
      next: () => {
        this.postingComment = false;
        this.commentForm.patchValue({ message: "" });
        this.commentForm.markAsPristine();
        this.commentForm.markAsUntouched();
        this.loadIssueComments(this.selectedIssueId as number);
      },
      error: (err) => {
        this.postingComment = false;
        this.commentsError =
          err?.error?.message ||
          err?.error?.detail ||
          "Failed to add comment. Please try again.";
      },
    });
  }

  deleteComment(item: IssueCommentItem): void {
    const id = Number(item?.id);
    if (!id) return;
    if (this.deletingCommentId) return;

    this.deletingCommentId = id;
    this.commentsError = "";

    this.issuesService.deleteIssueComment(id).subscribe({
      next: () => {
        this.deletingCommentId = null;
        if (this.selectedIssueId) this.loadIssueComments(this.selectedIssueId);
      },
      error: () => {
        this.deletingCommentId = null;
        this.commentsError = "Failed to delete comment.";
      },
    });
  }

  submitCreate(): void {
    if (this.creating) return;

    this.createError = "";

    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }

    const raw = this.createForm.getRawValue();

    const payload: CreateIssuePayload = {
      project: Number(this.projectId),
      agency: Number(raw.agency),
      tag: raw.tag as IssueTag,
      title: (raw.title || "").trim(),
      description: (raw.description || "").trim(),
      recipient_state: (raw.recipient_state || "").trim(),
      responsible_authority: (raw.responsible_authority || "").trim(),
      issue_category: (raw.issue_category || "").trim(),
      status: raw.status as IssueStatus,
    };

    if (!payload.project || !payload.agency) {
      this.createError = "Project / Agency is missing.";
      return;
    }

    this.creating = true;

    this.issuesService.createIssue(payload).subscribe({
      next: () => {
        this.creating = false;
        this.viewMode = "list";
        this.resetCreateState();
        this.resetCreateForm();
        this.loadIssues();
      },
      error: (err) => {
        this.creating = false;
        this.applyCreateServerErrors(err?.error);
        this.createError =
          err?.error?.message ||
          err?.error?.detail ||
          this.getCreateServerErrorSummary(err?.error) ||
          "Failed to create issue. Please try again.";
      },
    });
  }

  loadIssues(): void {
    if (!this.projectId) {
      this.issues = [];
      return;
    }

    const search = (this.filterForm.value?.search || "").trim();
    const status = (this.filterForm.value?.status || "").trim();

    this.loading = true;
    this.errorMsg = "";

    this.issuesService
      .listIssues({
        project: this.projectId,
        agency: this.agencyId || undefined,
        status: status || undefined,
        search: search || undefined,
      })
      .subscribe({
        next: (items) => {
          this.issues = items || [];
          this.loading = false;
          this.loadCommentsSummaryForIssues(this.issues);
        },
        error: (err) => {
          this.loading = false;
          this.errorMsg = err?.error?.message || "Failed to load issues. Please try again.";
        },
      });
  }

  loadCommentsSummaryForIssues(items: IssueItem[]): void {
    const ids = (items || [])
      .map((x) => Number(x?.id))
      .filter((x) => !!x);

    if (!ids.length) return;

    ids.forEach((id) => {
      this.commentsSummary[String(id)] = {
        count: this.commentsSummary[String(id)]?.count ?? 0,
        latest: this.commentsSummary[String(id)]?.latest ?? null,
        loading: true,
      };
    });

    const reqs = ids.map((id) =>
      this.issuesService.getIssueComments(id).pipe(
        map((list) => {
          const sorted = (list || []).slice().sort((a: any, b: any) => {
            const at = a?.created_at ? new Date(a.created_at).getTime() : 0;
            const bt = b?.created_at ? new Date(b.created_at).getTime() : 0;
            return bt - at;
          });
          return { issueId: id, count: sorted.length, latest: sorted[0] || null };
        }),
        catchError(() => of({ issueId: id, count: 0, latest: null }))
      )
    );

    forkJoin(reqs).subscribe((rows: any[]) => {
      (rows || []).forEach((r) => {
        this.commentsSummary[String(r.issueId)] = {
          count: Number(r.count || 0),
          latest: r.latest || null,
          loading: false,
        };
      });
    });
  }

  updateCommentsSummary(issueId: number, items: IssueCommentItem[]): void {
    const list = items || [];
    this.commentsSummary[String(issueId)] = {
      count: list.length,
      latest: list[0] || null,
      loading: false,
    };
  }

  loadAgencies(): void {
    if (this.agenciesLoading || this.agencies.length) return;

    this.agenciesLoading = true;
    this.agenciesError = "";

    this.issuesService.getAgencies().subscribe({
      next: (items) => {
        this.agencies = items || [];
        this.agenciesLoading = false;

        const pre = this.agencyId ? String(this.agencyId) : "";
        const hasPre = pre && this.agencies.some((a) => String(a.id) === pre);

        if (hasPre && !this.createForm.get("agency")?.value) {
          this.createForm.patchValue({ agency: pre });
        }
      },
      error: () => {
        this.agenciesLoading = false;
        this.agenciesError = "Failed to load agencies.";
      },
    });
  }

  getStatusClass(status?: IssueStatus): string {
    const s = (status || "").toLowerCase();
    if (s === "resolved") return "pill--green";
    if (s === "forwarded") return "pill--blue";
    return "pill--orange";
  }

  getTagClass(tag?: string): string {
    const t = (tag || "").toLowerCase();
    if (t === "pragati") return "badge--purple";
    return "badge--gray";
  }

  // getAuthor(item: IssueCommentItem): string {
  //   const u =
  //     item?.user_name ||
  //     item?.user?.name ||
  //     item?.user?.username ||
  //     item?.created_by?.name ||
  //     item?.created_by?.username;
  //   return (u || "User").toString();
  // }

  trackByIssue(index: number, item: IssueItem): any {
    return item?.id || index;
  }

  trackByComment(index: number, item: IssueCommentItem): any {
    return item?.id || index;
  }

  private getCreateDefaults() {
    return {
      project: this.projectId ? String(this.projectId) : "",
      agency: this.agencyId ? String(this.agencyId) : "",
      tag: "PMG",
      title: "",
      description: "",
      recipient_state: "",
      responsible_authority: "",
      issue_category: "",
      status: "Pending",
    };
  }

  private resetCreateForm(): void {
    const defaults = this.getCreateDefaults();

    this.createForm.reset(defaults);

    this.createForm.get("project")?.disable({ emitEvent: false });

    this.createForm.markAsPristine();
    this.createForm.markAsUntouched();

    Object.keys(this.createForm.controls).forEach((k) => {
      this.createForm.get(k)?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private resetCreateState(): void {
    this.creating = false;
    this.createError = "";
  }

  private applyCreateServerErrors(error: any): void {
    if (!error || typeof error !== "object") return;

    Object.keys(this.createForm.controls).forEach((field) => {
      const fieldError = error[field];
      if (!fieldError) return;

      const control = this.createForm.get(field);
      if (!control) return;

      control.setErrors({
        ...(control.errors || {}),
        server: Array.isArray(fieldError) ? fieldError.join(" ") : String(fieldError),
      });
      control.markAsTouched();
    });
  }

  private getCreateServerErrorSummary(error: any): string {
    if (!error || typeof error !== "object") return "";

    const firstKey = Object.keys(error).find((key) => !!error[key]);
    if (!firstKey) return "";

    const value = error[firstKey];
    const message = Array.isArray(value) ? value.join(" ") : String(value);
    return message ? `${this.toFieldLabel(firstKey)}: ${message}` : "";
  }

  private toFieldLabel(field: string): string {
    return field
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private resetDetailState(): void {
    this.selectedIssueId = null;
    this.issueDetail = null;
    this.detailLoading = false;
    this.detailError = "";
    this.comments = [];
    this.commentsLoading = false;
    this.commentsError = "";
    this.postingComment = false;
    this.deletingCommentId = null;

    this.commentForm.reset({ activity_type: "comment", message: "" });
    this.commentForm.markAsPristine();
    this.commentForm.markAsUntouched();
  }

  private resetAllStates(): void {
    this.resetCreateState();
    this.resetDetailState();
    this.resetCreateForm();
    this.agenciesLoading = false;
    this.agenciesError = "";
  }

  private toggleBodyScrollLock(lock: boolean): void {
    if (lock) this.renderer.addClass(document.body, "drawer-open");
    else this.renderer.removeClass(document.body, "drawer-open");
  }

  get cf() {
    return this.createForm.controls;
  }

  get comf() {
    return this.commentForm.controls;
  }
}
